// Client-side mirror of ewt-server's DisplayValue assembly
// (crates/ewt-server/src/display.rs, the same rules the /public/v1/displays/{token}/value
// endpoint applies): the template's own value, then a hard-coded fallback.
// Screens contribute nothing to the look or the copy — that is what makes a
// template previewable on its own. Pure and fetch-free — asset URLs are
// resolved by the caller (preview-panel.tsx, via the asset delivery manifest)
// and handed in already resolved, which is what keeps this synchronously
// testable with plain fixtures instead of a mocked asset client.

import type { Template, TemplateSection } from "@/api/types";
import type { DisplayDesign, DisplaySection, DisplayValue } from "./display-contract";

const DEFAULT_BACKGROUND_COLOR = "#101014";
const DEFAULT_LAYOUT = "column";
const DEFAULT_DISTRIBUTION = "center";
const PLACEHOLDER_NAME = "Preview";

// Mirrors crates/ewt-server/src/design.rs's default_sections() exactly — the
// sections a screen with no template (or a template with an empty section
// list) renders. The two must not drift: a change to design.rs's defaults
// must be made here too.
const DEFAULT_SECTIONS: TemplateSection[] = [
    { type: "text", id: "s1", text: "Estimated waiting time" },
    { type: "metric", id: "s2", unit: "min" },
];

/** The subset of a design template the preview draft actually reads. */
export type DraftTemplate = Pick<
    Template,
    "backgroundColor" | "layout" | "distribution" | "sections" | "fontStack" | "colorSlots" | "customCss"
>;

/**
 * Resolved artwork/font URLs for one draft. Optional and separate from
 * `template`/`screen` on purpose: this function never fetches anything, so
 * whatever the caller did (or didn't) resolve is passed straight through.
 * Every field defaults to `null` — a template with no image slot filled in,
 * or a deployment with no asset service configured, previews the same way
 * the display page renders it: without artwork, not with an error.
 */
export interface ResolvedAssetUrls {
    backgroundImageUrl?: string | null;
    /** Keyed by asset id, not section id — the same asset can be referenced
     *  by more than one section. */
    sectionImageUrls?: Map<string, string>;
    fontFaceUrl?: string | null;
}

export interface BuildDraftOptions {
    template?: DraftTemplate;
    valueMinutes: number;
    /** Renders the preview in the "stale" state the display page shows when
     *  the server-computed value has gone quiet. Default false. */
    stale?: boolean;
    assetUrls?: ResolvedAssetUrls;
}

/** A section as the admin stores it, turned into the shape a display
 *  renders: an image section carries its resolved URL instead of the asset
 *  reference it came from. Not found in the map (unresolved, or no asset
 *  referenced at all) previews with no artwork — the same degrade the
 *  display page itself falls back to. */
/** Present only when set, as the display endpoint sends it. */
function sizeOf(size: number | null | undefined): { size?: number } {
    return size == null ? {} : { size };
}

function toDisplaySection(section: TemplateSection, sectionImageUrls: Map<string, string>): DisplaySection {
    switch (section.type) {
        case "image":
            return {
                type: "image",
                id: section.id,
                assetUrl: section.assetId ? (sectionImageUrls.get(section.assetId) ?? null) : null,
            };
        case "metric":
            return { type: "metric", id: section.id, unit: section.unit, ...sizeOf(section.size) };
        case "text":
            return { type: "text", id: section.id, text: section.text, ...sizeOf(section.size) };
    }
}

/**
 * Builds one preview draft — the client-side stand-in for a real
 * `GET /public/v1/displays/{token}/value` response that drives the display page's
 * postMessage preview mode instead of its normal poll loop.
 *
 * `template` is optional so a not-yet-loaded (or brand-new) template
 * previews as the product defaults rather than as an error. The screen name
 * is a placeholder throughout: the display page carries it on the wire but
 * never renders it.
 */
export function buildDraft({ template, valueMinutes, stale = false, assetUrls = {} }: BuildDraftOptions): DisplayValue {
    const sectionImageUrls = assetUrls.sectionImageUrls ?? new Map<string, string>();
    // Only "no template at all" (undefined, or a stored `sections` of NULL)
    // previews the defaults, matching the server: a present template whose
    // section list is empty previews as background-only, same as the server
    // rendering an empty (but parseable) `sections` column with zero rows.
    const sections = template?.sections ?? DEFAULT_SECTIONS;

    const design: DisplayDesign = {
        backgroundColor: template?.backgroundColor ?? DEFAULT_BACKGROUND_COLOR,
        backgroundImageUrl: assetUrls.backgroundImageUrl ?? null,
        fontStack: template?.fontStack ?? null,
        fontFaceUrl: assetUrls.fontFaceUrl ?? null,
        colorSlots: template?.colorSlots ?? {},
        layout: template?.layout ?? DEFAULT_LAYOUT,
        distribution: template?.distribution ?? DEFAULT_DISTRIBUTION,
        sections: sections.map((section) => toDisplaySection(section, sectionImageUrls)),
        templateCss: template?.customCss ?? null,
    };

    return {
        screen: {
            name: PLACEHOLDER_NAME,
        },
        displayedMinutes: valueMinutes,
        computedAt: new Date().toISOString(),
        stale,
        design,
    };
}
